import { useReducer, useRef, useEffect } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import snakeCase from 'lodash/snakeCase';
import useEventCallback from '@seaweb/coral/hooks/useEventCallback';
import useFetch from './useFetch';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';

/**
 * @param {{ [key: string]: string|number }} params
 */
export const joinParams = (params) =>
  Object.keys(params)
    .map(
      (k) =>
        `${encodeURIComponent(snakeCase(k))}=${encodeURIComponent(params[k])}`
    )
    .join('&');

/**
 * Declarative get request - similar to Relay GraphQL.
 *
 * Normally, adding new option is not encouraged because most
 * of the times it leads to too many cases.
 * However, `skip` option is very important because there's no
 * way to prevent request being sent unless:
 *
 * - You don't mount the component - may be too restrictive
 *   especially for animation.
 *
 * - Get around with not updating state - this is of course
 *   a really bad solution because it complicates the logic
 *   and will still request at least once on mount.
 *
 * @param {string} url
 * @param {Object} [params]
 * @param {{ skip?: boolean }} [options]
 */
const useHttpGet = (url, params, options = {}) => {
  const paramsObj = params || {};

  const [state, dispatch] = useReducer(
    (st, action) => {
      switch (action.type) {
        case 'loading':
          return { ...st, loading: true, isRefresh: action.isRefresh };
        case 'success':
          return {
            ...st,
            loading: false,
            response: action.response,
            lastSuccessfulRequest: action.request,
          };
        case 'fail':
          return { ...st, loading: false, error: action.error };
        default:
          throw new Error('Unexpected action');
      }
    },
    {
      response: null,
      // sometimes you want to render something based on the request you sent
      // and display it altogether with the response, to avoid blinking twice
      lastSuccessfulRequest: null,
      error: null,
      // use different loading from useFetch to make it atomic
      loading: true,
    }
  );
  const { request } = useFetch();

  // abort is called if and only if it's unmounted
  // eslint-disable-next-line no-undef
  const abortRef = useRef(new AbortController());
  useEffect(() => () => abortRef.current.abort(), []);

  const get = useEventCallback(async (isRefresh = false) => {
    let query = '';
    if (Object.keys(paramsObj).length) {
      query = `?${joinParams(paramsObj)}`;
    }

    dispatch({ type: 'loading', isRefresh });

    try {
      const res = await request(`${url}${query}`, 'get', null, {
        signal: abortRef.current.signal,
      });
      dispatch({
        type: 'success',
        response: res.data,
        request: { url, params },
      });
    } catch (e) {
      if (!abortRef.current.signal.aborted) {
        dispatch({ type: 'fail', error: e });
      }
    }
  });

  const refresh = useEventCallback(() => {
    get(true);
  });

  useDeepCompareEffect(() => {
    if (!options.skip) {
      get();
    }
  }, [paramsObj, url, options]);

  return {
    ...state,
    refresh,
  };
};

export default useHttpGet;
